import { Link, useParams } from 'react-router-dom';
import { Head } from 'vite-react-ssg';
import { blogPosts } from '../constant/blogPosts';

const BASE_URL = 'https://poolshopsuryajayateknik.com';

const UBUD_AREA_PATTERN = /ubud|peliatan|sayan|kedewatan|singakerta|campuhan|lodtunduh|petulu|payangan|tegallalang|pengosekan|penestanan|sanggingan|kemenuh|kengetan|mas-gianyar|mas-ubud/i;

const BlogPost = () => {
  const { slug } = useParams();
  const post = blogPosts.find((p) => p.slug === slug);

  if (!post) return <div className="p-10 text-center">Post not found</div>;

  const metaDesc = post.excerpt || post.content.substring(0, 155).replace(/\s+/g, ' ').trim();
  const canonicalUrl = `${BASE_URL}/blog/${post.slug}`;
  const ogImage = post.imgSrc.startsWith('http') ? post.imgSrc : `${BASE_URL}${post.imgSrc}`;

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: post.title,
    datePublished: post.date,
    image: ogImage,
    author: { '@type': 'Organization', name: 'Surya Jaya Teknik' },
    publisher: {
      '@type': 'Organization',
      name: 'Pool Shop Surya Jaya Teknik',
      url: BASE_URL,
    },
    mainEntityOfPage: { '@type': 'WebPage', '@id': canonicalUrl },
  };

  const relatedPosts = blogPosts.filter((p) => p.slug !== slug).slice(0, 3);

  return (
    <div className="container mx-auto px-6 md:px-10 py-10 mt-10 max-w-3xl">
      <Head>
        <title>{post.title} | Blog Kolam Renang Bali</title>
        <meta name="description" content={metaDesc} />
        <link rel="canonical" href={canonicalUrl} />
        <meta property="og:title" content={`${post.title} | Blog Kolam Renang Bali`} />
        <meta property="og:description" content={metaDesc} />
        <meta property="og:image" content={ogImage} />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:type" content="article" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={`${post.title} | Blog Kolam Renang Bali`} />
        <meta name="twitter:description" content={metaDesc} />
        <script type="application/ld+json">{JSON.stringify(articleSchema)}</script>
      </Head>

      {/* Breadcrumb */}
      <nav className="text-sm text-gray-500 mb-6" aria-label="Breadcrumb">
        <ol className="flex items-center gap-1 flex-wrap">
          <li><Link to="/" className="hover:text-green-600 transition">Home</Link></li>
          <li className="mx-1">/</li>
          <li><Link to="/blog" className="hover:text-green-600 transition">Blog</Link></li>
          <li className="mx-1">/</li>
          <li className="text-gray-700 font-medium truncate max-w-xs">{post.title}</li>
        </ol>
      </nav>

      <h1 className="text-3xl md:text-4xl text-green-600 mb-4 leading-tight">{post.title}</h1>
      <div className="text-sm text-gray-500 mb-5">{post.date}</div>
      <img
        src={post.imgSrc}
        alt={`${post.title} - Pool Service Bali`}
        className="w-full max-w-3xl mb-6 rounded-lg"
      />
      <p className="text-gray-800 leading-8 whitespace-pre-line">
        {post.content}
      </p>

      {UBUD_AREA_PATTERN.test(post.slug) && (
        <div className="mt-10 p-6 bg-green-50 border border-green-200 rounded-xl">
          <p className="font-semibold text-green-800 mb-2">
            Butuh jasa perawatan kolam renang di Ubud?
          </p>
          <p className="text-sm text-gray-700 mb-3">
            Lihat halaman lengkap layanan kami khusus area Ubud dan Gianyar, termasuk
            harga, area layanan, dan FAQ.
          </p>
          <Link
            to="/jasa-perawatan-kolam-renang-ubud"
            className="inline-block bg-green-700 text-white text-sm font-semibold py-2 px-5 rounded-full hover:bg-green-800 transition"
          >
            Lihat Layanan Perawatan Kolam Ubud →
          </Link>
        </div>
      )}

      {/* Related Posts */}
      {relatedPosts.length > 0 && (
        <div className="mt-12 border-t border-gray-200 pt-8">
          <h2 className="text-xl font-bold text-gray-800 mb-5">Artikel Terkait</h2>
          <div className="grid sm:grid-cols-3 gap-4">
            {relatedPosts.map((rp) => (
              <Link
                key={rp.slug}
                to={`/blog/${rp.slug}`}
                className="group block rounded-xl overflow-hidden border border-gray-100 hover:shadow-md transition"
              >
                <img
                  src={rp.imgSrc}
                  alt={`${rp.title} - Blog Pool Bali`}
                  className="w-full h-28 object-cover"
                />
                <div className="p-3">
                  <p className="text-sm font-semibold text-gray-800 group-hover:text-green-600 transition leading-snug line-clamp-2">
                    {rp.title}
                  </p>
                  <p className="text-xs text-gray-400 mt-1">{rp.date}</p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default BlogPost;
